import toolsService from "../services/tools.service";
import { timeUnitsBetween, getLeadZero } from "./utils";

/**
 * Liczenie czasu wypowiedzi - w gore (bez limitu) i w dol (z limitem).
 *
 * Wspolne dla widoku wizualizacji (SpeekerModal) i nakladki transmisyjnej (OverlayView).
 * Oba ekrany pokazuja to samo posiedzenie, wiec musza pokazywac te sama liczbe - stad
 * jedno miejsce zamiast dwoch kopii tej samej matematyki.
 *
 * Metoda jest celowo odtworzona 1:1 z dotychczasowej implementacji wizualizacji,
 * razem z jej dwoma ograniczeniami:
 *
 *  - offset serwera dokladany jest jako godziny/minuty/sekundy, wiec skladowa `days`
 *    z `timeUnitsBetween` przepada. Ma to znaczenie dopiero przy rozjezdzie zegarow
 *    przekraczajacym dobe - przy realnym rozjezdzie rzedu sekund nie zmienia nic;
 *  - rozdzielczosc offsetu to 1 sekunda.
 *
 * To swiadomy kompromis: wynik ma byc identyczny z tym, co wizualizacja pokazuje dzis,
 * a nie "poprawniejszy". Zmiana tej matematyki zmieni liczby na obu ekranach naraz.
 */

/** Referencja neutralna - offset zerowy, czyli czas lokalny przegladarki. */
export function emptyServerReference() {
    return { frontTimestamp: null, serverTimestamp: null };
}

/**
 * Punkt odniesienia dla zegara serwera. Lokalny znacznik pobierany jest PRZED zapytaniem,
 * tak jak w dotychczasowej implementacji - czas przelotu wchodzi wiec do offsetu.
 *
 * Blad sieci nie moze zatrzymac licznika na wizji, wiec zamiast wyjatku wraca referencja
 * zerowa: licznik pojdzie po czasie lokalnym zamiast zamarznac.
 */
export async function captureServerReference() {
    const frontTimestamp = new Date();

    try {
        const response = await toolsService.getServerTime();

        if (response.status !== 200 || !response.data)
            return { frontTimestamp, serverTimestamp: frontTimestamp };

        const serverTimestamp = new Date(response.data);

        if (isNaN(serverTimestamp.getTime()))
            return { frontTimestamp, serverTimestamp: frontTimestamp };

        return { frontTimestamp, serverTimestamp };
    }
    catch (e) {
        console.log(e);
        return { frontTimestamp, serverTimestamp: frontTimestamp };
    }
}

/** Biezacy czas serwera odtworzony z referencji. */
export function serverNow(reference) {
    const internalDate = new Date();

    if (reference == null || reference.frontTimestamp == null || reference.serverTimestamp == null)
        return internalDate;

    const offset = timeUnitsBetween(reference.frontTimestamp, reference.serverTimestamp);

    internalDate.setHours(internalDate.getHours() + offset.hours);
    internalDate.setMinutes(internalDate.getMinutes() + offset.minutes);
    internalDate.setSeconds(internalDate.getSeconds() + offset.seconds);

    return internalDate;
}

/**
 * Etykieta HH:MM:SS bez znaku - prefiks "+" dokladaja konsumenci, bo wizualizacja
 * renderuje go osobnym elementem (inna kolorystyka niz sama liczba).
 */
export function formatDuration(diff) {
    if (diff == null)
        return "00:00:00";

    return `${getLeadZero(diff.hours + 24 * diff.days)}:${getLeadZero(diff.minutes)}:${getLeadZero(diff.seconds)}`;
}

/** Koniec wypowiedzi z limitem: start + przyznane sekundy. */
export function speechEndTime(startTime, seconds) {
    return new Date(startTime.getTime() + (seconds * 1000));
}

/** Liczenie w gore - od poczatku wypowiedzi do teraz. */
export function elapsedSince(startTime, reference) {
    const diff = timeUnitsBetween(startTime, serverNow(reference));

    return { diff, isOvertime: false, label: formatDuration(diff) };
}

/** Liczenie w dol - do konca przyznanego czasu; po przekroczeniu `isOvertime`. */
export function remainingUntil(endTime, reference) {
    const now = serverNow(reference);
    const isOvertime = (endTime - now) < 0;
    const diff = timeUnitsBetween(now, endTime);

    return { diff, isOvertime, label: formatDuration(diff) };
}

/**
 * Jedno wejscie dla obu trybow - konsument podaje obiekt mowcy z huba
 * (`startTime`, `isCountdown`, `seconds`) i dostaje gotowa etykiete.
 */
export function speechTime(speeker, reference) {
    if (speeker == null || speeker.startTime == null)
        return { diff: null, isOvertime: false, label: "00:00:00" };

    const startTime = new Date(Date.parse(speeker.startTime));

    if (isNaN(startTime.getTime()))
        return { diff: null, isOvertime: false, label: "00:00:00" };

    if (speeker.isCountdown)
        return remainingUntil(speechEndTime(startTime, speeker.seconds), reference);

    return elapsedSince(startTime, reference);
}
